import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Users, Phone, Mail, MapPin, Search, ShoppingBag } from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join('').toUpperCase();
};

const VendorCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/vendor/customers`, {
          withCredentials: true,
        });
        setCustomers(res.data?.customers || []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.name, c.phone, c.email, c.apartment_name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [customers, query]);

  // Aggregate stats
  const totals = useMemo(() => {
    const totalSpend = customers.reduce(
      (sum, c) => sum + Number(c.total_spent || 0),
      0
    );
    const totalOrders = customers.reduce(
      (sum, c) => sum + Number(c.total_orders || 0),
      0
    );
    return { totalSpend, totalOrders, customerCount: customers.length };
  }, [customers]);

  return (
    <div data-testid="vendor-customers-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">
          Customers
        </h1>
        <p className="font-body text-text-secondary">
          Neighbors who have ordered from you
        </p>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatTile
          label="Customers"
          value={totals.customerCount}
          icon={Users}
        />
        <StatTile
          label="Total orders"
          value={totals.totalOrders}
          icon={ShoppingBag}
        />
        <StatTile
          label="Total spent"
          value={`₹${totals.totalSpend.toFixed(0)}`}
          highlight
        />
      </div>

      {/* Search */}
      {customers.length > 0 && (
        <div className="relative mb-5">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, email, or apartment..."
            className="rounded-xl pl-9"
            data-testid="vendor-customers-search"
          />
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border p-12 flex flex-col items-center justify-center text-text-muted">
          <Users size={48} className="mb-4 opacity-40" />
          <p className="font-body text-lg">
            {customers.length === 0
              ? 'No customers yet'
              : 'No matches for that search'}
          </p>
          {customers.length === 0 && (
            <p className="font-body text-sm mt-1">
              Once neighbors place orders, they'll appear here.
            </p>
          )}
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="customers-grid"
        >
          {filtered.map((c) => (
            <CustomerCard key={c.id} customer={c} />
          ))}
        </div>
      )}
    </div>
  );
};

const StatTile = ({ label, value, icon: Icon, highlight = false }) => (
  <div
    className={`rounded-2xl border p-4 ${
      highlight
        ? 'bg-brand-primary/5 border-brand-primary/20'
        : 'bg-white border-surface-border'
    }`}
  >
    <div className="flex items-center gap-2 text-text-muted font-body text-xs uppercase tracking-wide">
      {Icon && <Icon size={14} />}
      {label}
    </div>
    <p
      className={`font-heading font-bold text-2xl mt-1 ${
        highlight ? 'text-brand-primary' : 'text-text-primary'
      }`}
    >
      {value}
    </p>
  </div>
);

const CustomerCard = ({ customer }) => (
  <div
    className="bg-white rounded-2xl border border-surface-border p-4 sm:p-5 hover:shadow-md transition-shadow"
    data-testid={`customer-card-${customer.id}`}
  >
    <div className="flex items-start gap-3 mb-3">
      <div className="w-11 h-11 rounded-full bg-brand-primary/10 text-brand-primary font-heading font-bold flex items-center justify-center shrink-0">
        {initials(customer.name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body font-semibold text-text-primary truncate">
          {customer.name}
        </p>
        {customer.apartment_name && (
          <p className="font-body text-xs text-text-muted inline-flex items-center gap-1 mt-0.5 truncate">
            <MapPin size={11} />
            {customer.apartment_name}
          </p>
        )}
      </div>
    </div>

    {/* Contact */}
    <div className="space-y-1.5 mb-3">
      {customer.phone && (
        <a
          href={`tel:${customer.phone}`}
          className="font-body text-sm text-text-secondary hover:text-brand-primary inline-flex items-center gap-2 transition-colors"
        >
          <Phone size={13} className="text-text-muted" />
          {customer.phone}
        </a>
      )}
      {customer.email && (
        <a
          href={`mailto:${customer.email}`}
          className="font-body text-sm text-text-secondary hover:text-brand-primary inline-flex items-center gap-2 transition-colors truncate w-full"
        >
          <Mail size={13} className="text-text-muted shrink-0" />
          <span className="truncate">{customer.email}</span>
        </a>
      )}
    </div>

    {/* Stats */}
    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-surface-border">
      <div>
        <p className="font-body text-[11px] uppercase tracking-wide text-text-muted">
          Orders
        </p>
        <p className="font-heading font-bold text-base text-text-primary">
          {customer.total_orders}
        </p>
        {customer.cancelled_count > 0 && (
          <p className="font-body text-[11px] text-text-muted">
            {customer.cancelled_count} cancelled
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="font-body text-[11px] uppercase tracking-wide text-text-muted">
          Spent
        </p>
        <p className="font-heading font-bold text-base text-brand-primary">
          ₹{Number(customer.total_spent || 0).toFixed(0)}
        </p>
        <p className="font-body text-[11px] text-text-muted">
          last {formatDate(customer.last_order_at)}
        </p>
      </div>
    </div>
  </div>
);

export default VendorCustomers;
